import {INCOME_INFLUENCE_PATH_LENGTH} from "../../../../configs/income";
import {
  selectHexes,
  selectPlayers,
  selectSpecialBuildings
} from "../../../../redux/reducers/sharedStateReducer/selectors";
import {selectConstructionPlanEntries} from "../../../../redux/reducers/privateStateReducer/selectors";
import {getHexDistance} from "../../../coords/getHexDistance";
import {calculateCitiesCache} from "../../calculateCitiesCache";
import {getGuildsAndTemplesWithChangedIncome} from "../../income/getGuildsAndTemplesWithChangedIncome";
import {checkIfCityGrowthToken, getCityGrowthAnimationTarget} from "../../city/getCityGrowthAnimationTarget";
import {checkIfConstructionPlanningAvailable, getConstructionPlanPlayer} from "./getConstructionPlanContext";
import {applyConstructionPlanEntriesToField} from "./buildConstructionPlanningViewState";
import {collectChangedHexKeys, INCOME_SPECIAL_BUILDING_TOKENS} from "./getPlannedGuildsAndTemplesIncomeChange";

/**
 * Что показать анимацией дохода после того, как игрок поставил строение в план
 * (placedEntry) — так же, как в свой ход после настоящей постройки:
 *  - placedIncomeBuilding — само запланированное, если это гильдия или храм:
 *    его доход и пути влияния (в свой ход — GameScene, ветка спецстроения);
 *  - affected — гильдии и храмы (построенные любого игрока и запланированные),
 *    чей доход изменило запланированное: пути влияния идут и через план (в свой
 *    ход — playNearbyGuildsTemplesIncomeChangeAnimation). isPlanned — гильдия
 *    или храм только в плане;
 *  - cityGrowth — город, которому досталась запланированная ячейка, и
 *    присоединенные ею рудники и руины (в свой ход — playCityGrowthAnimation,
 *    форма — как у getCityGrowthAnimationTarget).
 *
 * Доход и состав городов сравниваются на поле «с планом» до и после постановки
 * (entriesBefore — план до нее). Спецстроение поле не меняет: у него affected
 * всегда пуст, а cityGrowth — null.
 *
 * Вне планирования — null.
 *
 * @returns {null | {
 *   placedIncomeBuilding: null | {hexKey: string, buildingType: string},
 *   affected: Array<{hexKey: string, buildingType: string, isPlanned: boolean}>,
 *   cityGrowth: null | {playerColor: string, cityHexKeys: string[],
 *     keyPoints: Array<{hexKey: string, tokenType: string}>},
 *   player: Object,
 *   hexes: Object,
 *   specialBuildings: Object,
 *   citiesCache: null | Object,
 * }} citiesCache и поле «с планом» после постановки — для самой анимации
 *   (citiesCache — только если показывать есть что)
 */
export const getPlanPlacementIncomeChange = (state, placedEntry, entriesBefore) => {
  if (!checkIfConstructionPlanningAvailable(state) || !placedEntry) {
    return null;
  }

  const hexes = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);
  const fieldBefore = applyConstructionPlanEntriesToField(hexes, specialBuildings, entriesBefore || []);
  const fieldAfter = applyConstructionPlanEntriesToField(hexes, specialBuildings, selectConstructionPlanEntries(state));

  // Кэши городов «с планом» — полный обход поля каждый: считаются, только
  // когда показывать есть что, и не больше раза на постановку
  let citiesCacheBefore = null;
  let citiesCacheAfter = null;
  const getCitiesCacheBefore = () => {
    citiesCacheBefore = citiesCacheBefore || calculateCitiesCache(fieldBefore.hexes, fieldBefore.specialBuildings);
    return citiesCacheBefore;
  };
  const getCitiesCacheAfter = () => {
    citiesCacheAfter = citiesCacheAfter || calculateCitiesCache(fieldAfter.hexes, fieldAfter.specialBuildings);
    return citiesCacheAfter;
  };

  const result = {
    placedIncomeBuilding: INCOME_SPECIAL_BUILDING_TOKENS.includes(placedEntry.token)
      ? {hexKey: placedEntry.hexKey, buildingType: placedEntry.token}
      : null,
    affected: [],
    cityGrowth: null,
    player: getConstructionPlanPlayer(state),
    hexes: fieldAfter.hexes,
    specialBuildings: fieldAfter.specialBuildings,
    citiesCache: null,
  };

  if (checkIfCityGrowthToken(placedEntry.token)) {
    result.cityGrowth = getCityGrowthAnimationTarget(
      placedEntry.hexKey, fieldAfter.hexes[placedEntry.hexKey], getCitiesCacheBefore(), getCitiesCacheAfter(),
      fieldAfter.hexes
    );
  }

  // Ранний выход до пересчета городов (полный обход поля): ни одна гильдия или
  // храм не дотягивается до изменившихся ячеек
  const changedHexKeys = collectChangedHexKeys(fieldBefore.hexes, fieldAfter.hexes);
  const incomeBuildingsWithinReach = Object.fromEntries(Object.entries(fieldAfter.specialBuildings)
    .filter(([hexKey, buildingType]) => (
      INCOME_SPECIAL_BUILDING_TOKENS.includes(buildingType)
      && changedHexKeys.some((changedHexKey) => getHexDistance(changedHexKey, hexKey) <= INCOME_INFLUENCE_PATH_LENGTH)
    )));

  if (Object.keys(incomeBuildingsWithinReach).length > 0) {
    result.affected = getGuildsAndTemplesWithChangedIncome(
      placedEntry.hexKey,
      fieldBefore.hexes,
      fieldAfter.hexes,
      incomeBuildingsWithinReach,
      selectPlayers(state),
      getCitiesCacheBefore(),
      getCitiesCacheAfter(),
      {changedHexKeys},
    ).map((building) => ({...building, isPlanned: specialBuildings[building.hexKey] !== building.buildingType}));
  }

  if (result.placedIncomeBuilding || result.affected.length > 0 || result.cityGrowth) {
    result.citiesCache = getCitiesCacheAfter();
  }
  return result;
};
